import React from 'react';
import { motion } from 'motion/react';
import {
  Monitor,
  Tablet,
  Smartphone,
  Palette,
  Sun,
  Moon,
  MonitorCog,
  PanelLeft,
} from 'lucide-react';
import Section from '../layout/Section';
import SectionTitle from '../ui/SectionTitle';
import ScrollReveal from '../animations/ScrollReveal';
import StaggerContainer, { staggerItem } from '../animations/StaggerContainer';
import DeviceFrame from '../ui/DeviceFrame';
import { colorPalettes } from '../../data/deviceShowcase';

import desktopImg from '../../assets/img/responsive/desktop.png';
import tabletteImg from '../../assets/img/responsive/tablette.png';
import mobileImg from '../../assets/img/responsive/mobile.jpeg';
import paletteImg from '../../assets/img/responsive/palette-de-couleur.png';

const infoCards = [
  {
    icon: Monitor,
    title: 'Barre latérale',
    description: 'Navigation complète sur grand écran avec sidebar permanente',
  },
  {
    icon: Tablet,
    title: 'Format tablette',
    description: 'Interface adaptée pour une utilisation en boutique',
  },
  {
    icon: Smartphone,
    title: 'Menu hamburger',
    description: 'Navigation compacte et intuitive sur mobile',
  },
];

const themeChips = [
  { label: 'Light', icon: Sun },
  { label: 'Dark', icon: Moon },
  { label: 'Auto', icon: MonitorCog },
];

export default function DeviceShowcaseSection() {
  return (
    <Section dark id="device-showcase">
      {/* ── Acte 1 : Responsive multi-plateforme ── */}
      <div className="mb-10 sm:mb-20 lg:mb-28">
        <div className="text-center mb-3">
          <span className="inline-block px-4 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase bg-primary/10 text-primary border border-primary/20">
            Multi-plateforme
          </span>
        </div>

        <SectionTitle
          title="Votre boutique, partout avec vous"
          subtitle="Une seule application qui s'adapte parfaitement à tous vos écrans — du smartphone au desktop."
          dark
        />

        {/* ── Desktop layout (lg+): Perspective composition ── */}
        <div className="hidden lg:flex items-end justify-center mt-12 relative">
          {/* iPad - left, tucked behind MacBook */}
          <ScrollReveal direction="left" delay={0.1} className="relative z-10" style={{ marginRight: 100, marginBottom: 24 }}>
            <DeviceFrame
              device="iPad Mini"
              color="black"
              landscape
              zoom={0.25}
              imgPadding={8}
              screenshot={tabletteImg}
              alt="XamXamBoutik sur tablette"
              label="Tablette"
            />
          </ScrollReveal>

          {/* MacBook - center, in front */}
          <ScrollReveal direction="up" delay={0} className="relative z-20">
            <DeviceFrame
              device="MacBook Pro"
              zoom={0.45}
              imgPadding={8}
              screenshot={desktopImg}
              alt="XamXamBoutik sur desktop"
              label="Desktop"
            />
          </ScrollReveal>

          {/* iPhone - right, tucked behind MacBook */}
          <ScrollReveal direction="right" delay={0.2} className="relative z-10" style={{ marginLeft: 100, marginBottom: 24 }}>
            <DeviceFrame
              device="iPhone X"
              zoom={0.3}
              imgPadding={8}
              screenshot={mobileImg}
              alt="XamXamBoutik sur mobile"
              label="Mobile"
            />
          </ScrollReveal>
        </div>

        {/* ── Tablet layout (md only): Stacked ── */}
        <div className="hidden md:flex lg:hidden flex-col items-center mt-10">
          <ScrollReveal direction="up">
            <DeviceFrame
              device="MacBook Pro"
              zoom={0.25}
              screenshot={desktopImg}
              alt="XamXamBoutik sur desktop"
              label="Desktop"
            />
          </ScrollReveal>
          <div className="flex items-end justify-center" style={{ marginTop: -10 }}>
            <ScrollReveal direction="left" delay={0.1} style={{ marginRight: 20 }}>
              <DeviceFrame
                device="iPad Mini"
                color="black"
                landscape
                zoom={0.18}
  
                screenshot={tabletteImg}
                alt="XamXamBoutik sur tablette"
                label="Tablette"
              />
            </ScrollReveal>
            <ScrollReveal direction="right" delay={0.2} style={{ marginLeft: 20 }}>
              <DeviceFrame
                device="iPhone X"
                zoom={0.18}
                screenshot={mobileImg}
                alt="XamXamBoutik sur mobile"
                label="Mobile"
              />
            </ScrollReveal>
          </div>
        </div>

        {/* ── Mobile layout (<md): Horizontal carousel ── */}
        <div className="md:hidden mt-10 -mx-4">
          <div className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide gap-4 px-4 pb-4">
            {[
              {
                device: 'MacBook Pro' as const,
                zoom: 0.3,
                img: desktopImg,
                alt: 'XamXamBoutik sur desktop',
                label: 'Desktop',
              },
              {
                device: 'iPad Mini' as const,
                color: 'black' as const,
                landscape: true,

                zoom: 0.35,
                img: tabletteImg,
                alt: 'XamXamBoutik sur tablette',
                label: 'Tablette',
              },
              {
                device: 'iPhone X' as const,
                zoom: 0.4,
                img: mobileImg,
                alt: 'XamXamBoutik sur mobile',
                label: 'Mobile',
              },
            ].map((item) => (
              <div
                key={item.device}
                className="flex-shrink-0 w-[85vw] snap-center flex justify-center"
              >
                <DeviceFrame
                  device={item.device}
                  color={item.color}
                  landscape={'landscape' in item ? (item as any).landscape : undefined}
                  objectFit={'objectFit' in item ? (item as any).objectFit : undefined}
                  zoom={item.zoom}
                  screenshot={item.img}
                  alt={item.alt}
                  label={item.label}
                />
              </div>
            ))}
          </div>
        </div>

        {/* ── Info cards ── */}
        <StaggerContainer className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-2 sm:mt-12 max-w-4xl mx-auto">
          {infoCards.map((card) => (
            <motion.div
              key={card.title}
              variants={staggerItem}
              className="glass rounded-2xl p-5 text-center card-shimmer card-glow"
            >
              <card.icon className="w-8 h-8 text-primary mx-auto mb-3" />
              <h4 className="text-white font-semibold mb-1">{card.title}</h4>
              <p className="text-gray-400 text-sm">{card.description}</p>
            </motion.div>
          ))}
        </StaggerContainer>
      </div>

      {/* ── Acte 2 : Personnalisation ── */}
      <div>
        <div className="text-center mb-3">
          <span className="inline-block px-4 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase bg-primary/10 text-primary border border-primary/20">
            Personnalisation
          </span>
        </div>

        <SectionTitle
          title="10 palettes de couleurs. Votre marque, votre style."
          subtitle="Mode clair, sombre ou automatique — et une palette qui correspond à votre identité visuelle."
          dark
        />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 mt-8 sm:mt-12 items-center">
          {/* ── Left: Device with palette screenshot ── */}
          <ScrollReveal direction="left" className="flex justify-center relative">
            <DeviceFrame
              device="iPhone X"
              zoom={0.45}
              screenshot={paletteImg}
              alt="Paramètres de thèmes XamXamBoutik"
              label="Paramètres"
            />
            {/* Floating badge */}
            <div className="absolute -top-2 -right-2 sm:top-4 sm:right-4 lg:right-0 animate-float">
              <div className="glass rounded-xl px-4 py-2 flex items-center gap-2 border-primary/20">
                <Palette className="w-5 h-5 text-primary" />
                <span className="text-white text-sm font-semibold">10 thèmes</span>
              </div>
            </div>
          </ScrollReveal>

          {/* ── Right: Theme details ── */}
          <ScrollReveal direction="right" delay={0.1}>
            {/* Light / Dark / Auto chips */}
            <div className="flex flex-wrap gap-3 mb-8">
              {themeChips.map((chip) => (
                <div
                  key={chip.label}
                  className="glass rounded-full px-4 py-2 flex items-center gap-2"
                >
                  <chip.icon className="w-4 h-4 text-primary" />
                  <span className="text-white text-sm font-medium">{chip.label}</span>
                </div>
              ))}
            </div>

            {/* Color palette grid 2×5 */}
            <StaggerContainer className="grid grid-cols-2 gap-3 mb-8">
              {colorPalettes.map((palette) => (
                <motion.div
                  key={palette.name}
                  variants={staggerItem}
                  className="glass rounded-xl px-3 py-2.5 flex items-center gap-3 card-shimmer"
                >
                  {/* Bicolor swatch */}
                  <div className="w-8 h-8 rounded-full flex-shrink-0 relative overflow-hidden">
                    <div
                      className="absolute inset-0"
                      style={{
                        background: `linear-gradient(135deg, ${palette.colors[0]} 50%, ${palette.colors[1]} 50%)`,
                      }}
                    />
                  </div>
                  <span className="text-white text-xs sm:text-sm font-medium truncate">
                    {palette.name}
                  </span>
                </motion.div>
              ))}
            </StaggerContainer>

            {/* Callout: Navigation adaptative */}
            <div className="glass rounded-2xl p-5 flex items-start gap-4 card-glow">
              <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <PanelLeft className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h4 className="text-white font-semibold mb-1">Navigation adaptative</h4>
                <p className="text-gray-400 text-sm">
                  Barre de navigation en haut ou sur le côté gauche — XamXamBoutik s'adapte
                  automatiquement selon la taille de l'écran pour une ergonomie optimale.
                </p>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </Section>
  );
}
